import { BaseEntity, type ValidationResult } from '@memberjunction/core';
import { RegisterClass } from '@memberjunction/global';
import { FeedbackPlatformReviewEntity, FeedbackPlatformReviewResponseEntity } from 'mj_generatedentities';
import { DecideReviewChange, DecideReviewResponseChange, type OEPReviewStatus } from '../rules/decisions.js';
import { OEP_ENTITY } from '../contracts/entities.js';
import { Apply, BeginRules, CurrentString, TrustedID } from './shared.js';

const REVIEW_STATUSES: readonly OEPReviewStatus[] = ['NotStarted', 'InProgress', 'Completed', 'Submitted'];
function AsReviewStatus(v: string | null): OEPReviewStatus | null {
    return REVIEW_STATUSES.find((s) => s === v) ?? null;
}

@RegisterClass(BaseEntity, OEP_ENTITY.Reviews)
export class OEPReviewEntity extends FeedbackPlatformReviewEntity {
    override get DefaultSkipAsyncValidation(): boolean { return false; }
    override async ValidateAsync(): Promise<ValidationResult> {
        const result = await super.ValidateAsync();
        const rc = await BeginRules(this, this.ActiveUser, result, 'Reviews', this.ID, { ReviewAssignmentID: this.ReviewAssignmentID }, ['Status']);
        if (!rc) return result;
        const assignmentID = TrustedID(rc, 'ReviewAssignmentID', this.ReviewAssignmentID);
        const ctx = await rc.Data.PresentationForAssignment(assignmentID);
        if (!ctx || !ctx.SeasonID || !ctx.Phase) return result;
        Apply(result, DecideReviewChange({
            Phase: ctx.Phase, IsStaffOrSystem: rc.IsStaff, IsNew: !this.IsSaved,
            // Old status from the fresh DB row — the client supplies OldValue when TrackRecordChanges=0.
            OldStatus: AsReviewStatus(CurrentString(rc, 'Status')), NewStatus: this.Status,
            ExistingForAssignment: this.IsSaved ? 1 : await rc.Data.ReviewCountForAssignment(assignmentID),
            ResponsesComplete: this.Status === 'Submitted' && this.IsSaved ? await rc.Data.ReviewResponsesComplete(this.ID) : false,
        }), this.Status);
        return result;
    }
}

@RegisterClass(BaseEntity, OEP_ENTITY.ReviewResponses)
export class OEPReviewResponseEntity extends FeedbackPlatformReviewResponseEntity {
    override get DefaultSkipAsyncValidation(): boolean { return false; }
    override async ValidateAsync(): Promise<ValidationResult> {
        const result = await super.ValidateAsync();
        const rc = await BeginRules(this, this.ActiveUser, result, 'ReviewResponses', this.ID, { ReviewID: this.ReviewID, ReviewQuestionID: this.ReviewQuestionID });
        if (!rc) return result;
        const ctx = await rc.Data.PresentationForReview(TrustedID(rc, 'ReviewID', this.ReviewID));
        if (!ctx || !ctx.SeasonID || !ctx.Phase) return result;
        const range = await rc.Data.QuestionRange(TrustedID(rc, 'ReviewQuestionID', this.ReviewQuestionID));
        Apply(result, DecideReviewResponseChange({
            Phase: ctx.Phase, IsStaffOrSystem: rc.IsStaff, ReviewStatus: ctx.ReviewStatus,
            NumericValue: this.NumericValue ?? null, Min: range.Min, Max: range.Max,
        }), this.NumericValue);
        return result;
    }
}
